// Global error suppression utility for common non-critical errors
export const suppressResizeObserverErrors = () => {
  // Store original handlers
  const originalConsoleError = console.error;
  const originalWindowError = window.onerror;
  const originalUnhandledRejection = window.onunhandledrejection;

  // Store event listeners for cleanup
  const errorListeners = [];

  // Helper function to check if error is ResizeObserver related
  const isResizeObserverError = (error) => {
    const errorMessage = error?.toString() || error?.message?.toString() || '';
    return errorMessage.includes('ResizeObserver loop completed with undelivered notifications') ||
           errorMessage.includes('ResizeObserver loop limit exceeded');
  };

  // Override console.error
  console.error = (...args) => {
    const errorMessage = args[0]?.toString() || '';
    
    // Suppress ResizeObserver errors
    if (isResizeObserverError(errorMessage)) {
      return;
    }
    
    // Call original handler for other errors
    originalConsoleError.apply(console, args);
  };

  // Override window.onerror
  window.onerror = (message, source, lineno, colno, error) => {
    if (isResizeObserverError(message) || isResizeObserverError(error)) {
      return true; // Prevent default error handling
    }
    
    if (originalWindowError) {
      return originalWindowError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Override unhandled promise rejections
  window.onunhandledrejection = (event) => {
    if (isResizeObserverError(event.reason)) {
      event.preventDefault();
      return;
    }
    
    if (originalUnhandledRejection) {
      originalUnhandledRejection(event);
    }
  };

  // Add event listeners for additional error catching
  const errorEventListener = (event) => {
    if (isResizeObserverError(event.error) || isResizeObserverError(event.message)) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  };

  const unhandledRejectionListener = (event) => {
    if (isResizeObserverError(event.reason)) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  };

  // Add event listeners
  window.addEventListener('error', errorEventListener, true);
  window.addEventListener('unhandledrejection', unhandledRejectionListener, true);
  errorListeners.push(
    () => window.removeEventListener('error', errorEventListener, true),
    () => window.removeEventListener('unhandledrejection', unhandledRejectionListener, true)
  );

  // Also try to catch and suppress the specific error at the source
  const originalResizeObserver = window.ResizeObserver;
  if (originalResizeObserver) {
    window.ResizeObserver = class extends originalResizeObserver {
      constructor(callback) {
        const wrappedCallback = (entries, observer) => {
          try {
            // Use requestAnimationFrame to prevent loop issues
            requestAnimationFrame(() => {
              try {
                callback(entries, observer);
              } catch (error) {
                if (!isResizeObserverError(error)) {
                  throw error;
                }
              }
            });
          } catch (error) {
            if (!isResizeObserverError(error)) {
              throw error;
            }
          }
        };
        
        super(wrappedCallback);
      }
    };
  }

  return () => {
    // Cleanup function to restore original handlers
    console.error = originalConsoleError;
    window.onerror = originalWindowError;
    window.onunhandledrejection = originalUnhandledRejection;
    
    // Remove event listeners
    errorListeners.forEach(cleanup => cleanup());
    
    if (originalResizeObserver) {
      window.ResizeObserver = originalResizeObserver;
    }
  };
};

// Alternative approach: Use requestAnimationFrame to debounce ResizeObserver
export const debounceResizeObserver = (callback, delay = 16) => {
  let timeoutId;
  let rafId;
  
  return (...args) => {
    clearTimeout(timeoutId);
    cancelAnimationFrame(rafId);
    
    rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        try {
          callback(...args);
        } catch (error) {
          if (!error.message?.includes('ResizeObserver loop completed with undelivered notifications')) {
            throw error;
          }
        }
      }, delay);
    });
  };
};
